"use client";
import type { Route } from "next";

import Link from "next/link";
import type { Locale } from "@/lib/i18n/locales";
import { languageDestination } from "@/lib/i18n/navigation";
import rolloutCopy from "@/messages/mandarin-rollout.json";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { PointerLockControls } from "three/addons/controls/PointerLockControls.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";

import TourOrientation from "./TourOrientation";
import { EXPERIMENTS_AVAILABLE } from "./availability";

import ExperimentRuntime from "./experiments/ExperimentRuntime";
import TitrationReadingView, {
  practiceReadingSample,
} from "./experiments/TitrationReadingView";
import TitrationGuidedCard from "./experiments/TitrationGuidedCard";
import {
  TitrationGuidedStep,
  createInitialTitrationGuidedState,
  displayedGuidedSnapshot,
  getTitrationGuidedStepDefinition,
  targetLabelFor,
  titrationGuidedReducer,
  type TitrationGuidedAction,
} from "./experiments/TitrationGuidedController";
import TitrationSpatialOverlay, {
  type OverlayPoint,
  type OverlayRect,
  type TitrationOverlayProjection,
  type TitrationSpatialOverlayHandle,
} from "./experiments/TitrationSpatialOverlay";
import {
  createTitrationWorkstation3D,
  pickTitrationInteraction,
  type TitrationOverlayFocus,
} from "./experiments/TitrationWorkstation3D";
import { buretteReadingMl, type ExperimentId } from "./experiments/models";
import {
  createInitialExperimentSessions,
  type ElectricalSessionState,
} from "./experiments/session";
import {
  createHendersonLightingRig,
  hendersonLightingModeFromSearch,
} from "./lighting";
import {
  applyImportedModelRendering,
  createHendersonRenderEnvironment,
  setImportedModelVisibility,
  setTitrationBenchClearance,
  type HendersonRenderEnvironment,
} from "./rendering";
import styles from "./virtual-tour.module.css";

const ASSET_ROOT = "/practicals/henderson-road/virtual-tour/models";
const CUTAWAY_MODEL = `${ASSET_ROOT}/henderson-lab-full-unit-cutaway.glb`;
const FULL_MODEL = `${ASSET_ROOT}/henderson-lab-full-unit.glb`;

const EYE_HEIGHT = 1.62;
const PLAYER_RADIUS = 0.27;
const WALK_SPEED = 2.05;
const FAST_SPEED = 3.15;
const JUMP_VELOCITY = 4.8;
const GRAVITY = 11.5;
const COLLISION_VERTICAL_CLEARANCE = 0.025;
const GROUND_SNAP_TOLERANCE = 0.035;
const MOBILE_STEER_RATE = 1.05;
const WALK_DESKTOP_FOV = 58;
const WALK_TARGET_HORIZONTAL_FOV = 62;
const WALK_MAX_VERTICAL_FOV = 100;

type Mode = "orbit" | "walk";
type EngineMode = Mode | "walk-loading" | "experiment";
type Obstacle = {
  name: string;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
  landable: boolean;
};
type GuideView = { position: THREE.Vector3; target: THREE.Vector3 };
const B = (x: number, y: number, height = EYE_HEIGHT) =>
  new THREE.Vector3(x, height, -y);

const GUIDE_VIEWS: Record<string, GuideView> = {
  "Wet Lab": {
    position: B(4.55, 1.78, 1.85),
    target: B(2.65, 6.0, 1.05),
  },
  "Dry Lab": {
    position: B(7.72, 2.18, 1.8),
    target: B(6.5, 6.1, 1.0),
  },
  "Shower / Eyewash": {
    // Stay on the safety-bay side of the corrected wall at plan y = 9.30 m.
    position: B(5.35, 10.22, 1.65),
    target: B(7.88, 10.3, 1.23),
  },
  Prep: {
    position: B(5.15, -0.25, 1.72),
    target: B(2.6261, -1.835, 1.3768),
  },
  Entrance: {
    position: B(7.45, -1.05, 1.68),
    target: B(6.65, 0.85, 1.25),
  },
  Windows: {
    position: B(4.35, 6.0, 1.68),
    target: B(2.15, 10.25, 1.03),
  },
};
const GUIDE_LABELS = ["Overview", ...Object.keys(GUIDE_VIEWS)];

const EXPERIMENT_STATIONS: Record<
  ExperimentId,
  { title: string; station: string; position: THREE.Vector3 }
> = {
  titration: {
    title: "Acid–base titration",
    station: "Wet Lab student bench",
    position: B(3.45, 4.75, EYE_HEIGHT),
  },
  electrical: {
    title: "Filament-lamp I–V",
    station: "Dry Lab table",
    position: B(7.05, 3.7, EYE_HEIGHT),
  },
};
const EXPERIMENT_PROXIMITY_M = 1.7;
const TITRATION_WORKSTATION_POSITION = new THREE.Vector3(2.335, 0.902, -6.05);
const TITRATION_WORKSTATION_VIEW: GuideView = {
  position: new THREE.Vector3(3.65, 1.52, -4.65),
  target: new THREE.Vector3(2.36, 1.27, -6.01),
};
const TITRATION_WORKSTATION_PORTRAIT_VIEW: GuideView = {
  position: new THREE.Vector3(3.65, 1.52, -4.65),
  target: new THREE.Vector3(2.36, 1.27, -6.01),
};
const TITRATION_DESKTOP_FOV = 38;
const TITRATION_MOBILE_FOV = 58;
const TITRATION_PORTRAIT_FOV = 78;
const TITRATION_MAX_FLOW_ML_PER_SECOND = 2;
const TITRATION_FLOW_COMMIT_SECONDS = 0.06;

type TourController = {
  orientation: (open: boolean) => void;
  orbit: () => void;
  walk: () => void;
  jump: () => void;
  reset: () => void;
  experiment: (experimentId: ExperimentId) => void;
  exitExperiment: () => void;
  focusTitration: (focus: TitrationOverlayFocus) => void;
  guide: (label: string) => void;
};
function normalizedObjectName(name: string) {
  return name.toLowerCase().replace(/_/g, " ");
}

function isLandableName(name: string) {
  const normalized = normalizedObjectName(name);
  return (
    normalized.includes("worktop") ||
    normalized.includes("table top") ||
    /dry table \d+ top/.test(normalized)
  );
}

function isColliderName(name: string) {
  const normalized = normalizedObjectName(name);
  if (
    normalized.includes("fan ") ||
    normalized.includes("wall cabinet") ||
    normalized.includes("whiteboard") ||
    normalized.includes("handle") ||
    normalized.includes("extinguisher")
  ) {
    return false;
  }
  if (/master d[1-5] door/.test(normalized)) return true;

  return (
    normalized.includes(" wall") ||
    normalized.includes("partition") ||
    normalized.includes("worktop") ||
    normalized.includes("table top") ||
    /dry table \d+ top/.test(normalized) ||
    normalized.includes("base cabinet") ||
    (normalized.includes("island") && normalized.includes("cabinet")) ||
    normalized.includes("dry store") ||
    normalized.includes("flammable cabinet") ||
    normalized.includes("acids cabinet") ||
    normalized.includes("shower riser") ||
    normalized.includes("shower base")
  );
}

function disposeRoot(root: THREE.Object3D | null) {
  if (!root) return;
  root.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry?.dispose();
    const materials = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];
    materials.forEach((material) => material?.dispose());
  });
}
export default function HendersonVirtualTour({
  locale = "en",
}: {
  locale?: Locale;
}) {
  const text = (value: keyof typeof rolloutCopy.virtualTour.text) =>
    locale === "zh-Hans" ? rolloutCopy.virtualTour.text[value] : value;
  const labDestination = languageDestination("/locations/henderson", locale);
  const orbitButtonRef = useRef<HTMLButtonElement>(null);
  const walkButtonRef = useRef<HTMLButtonElement>(null);
  const viewsRef = useRef<HTMLElement>(null);
  const orientationOpenRef = useRef(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const joystickRef = useRef<HTMLDivElement>(null);
  const joystickThumbRef = useRef<HTMLDivElement>(null);
  const lookPadRef = useRef<HTMLDivElement>(null);
  const titrationOverlayRef = useRef<TitrationSpatialOverlayHandle>(null);
  const controllerRef = useRef<TourController | null>(null);

  const [ready, setReady] = useState(false);
  const [mode, setMode] = useState<Mode>("orbit");
  const [walkLoading, setWalkLoading] = useState(false);
  const [mobileWalkActive, setMobileWalkActive] = useState(false);
  const [lookEngaged, setLookEngaged] = useState(false);
  const [experimentMenuOpen, setExperimentMenuOpen] = useState(false);
  const [readingViewOpen, setReadingViewOpen] = useState(false);
  const [practiceReadingMl, setPracticeReadingMl] = useState<number | null>(
    null
  );
  const [activeExperiment, setActiveExperiment] = useState<ExperimentId | null>(
    null
  );
  const activeExperimentRef = useRef(activeExperiment);
  activeExperimentRef.current = activeExperiment;
  const [titrationGuided, dispatchTitrationBase] = useReducer(
    titrationGuidedReducer,
    undefined,
    () =>
      createInitialTitrationGuidedState(
        createInitialExperimentSessions().titration
      )
  );
  const titrationGuidedRef = useRef(titrationGuided);
  titrationGuidedRef.current = titrationGuided;
  const dispatchTitrationGuided = useCallback(
    (action: TitrationGuidedAction) => {
      titrationGuidedRef.current = titrationGuidedReducer(
        titrationGuidedRef.current,
        action
      );
      dispatchTitrationBase(action);
    },
    [dispatchTitrationBase]
  );
  const [electricalSession, setElectricalSession] = useState(
    () => createInitialExperimentSessions().electrical
  );
  const electricalSessionRef = useRef(electricalSession);
  electricalSessionRef.current = electricalSession;
  const [nearbyExperiment, setNearbyExperiment] = useState<ExperimentId | null>(
    null
  );
  const [resumeWalkPrompt, setResumeWalkPrompt] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Loading 3D overview…");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [status, setStatus] = useState({
    title: "Orbit mode",
    detail: "Drag to rotate · scroll to zoom",
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    const joystick = joystickRef.current;
    const joystickThumb = joystickThumbRef.current;
    const lookPad = lookPadRef.current;
    if (!canvas || !joystick || !joystickThumb || !lookPad) return;

    // Keep stable non-null aliases for nested event handlers.
    const canvasElement = canvas;
    const joystickElement = joystick;
    const joystickThumbElement = joystickThumb;
    const lookPadElement = lookPad;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    let disposed = false;
    let preloadTimer = 0;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xe9ebe7);
    scene.fog = new THREE.Fog(0xe9ebe7, 28, 52);

    const camera = new THREE.PerspectiveCamera(
      WALK_DESKTOP_FOV,
      window.innerWidth / window.innerHeight,
      0.04,
      120
    );
    camera.position.set(13, 10, 13);
    camera.rotation.order = "YXZ";

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        powerPreference: "high-performance",
      });
    } catch (error) {
      console.error(error);
      setLoadError("This browser could not start the 3D viewer.");
      setLoadingMessage("");
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    const lightingMode = hendersonLightingModeFromSearch(
      window.location.search
    );
    const lightingRig = createHendersonLightingRig(scene, lightingMode);
    renderer.toneMappingExposure = lightingRig.toneMappingExposure;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    let renderEnvironment: HendersonRenderEnvironment | null = null;
    try {
      renderEnvironment = createHendersonRenderEnvironment(renderer);
      scene.environment = renderEnvironment.texture;
      scene.environmentIntensity = lightingRig.environmentIntensity;
    } catch (error) {
      console.warn("Henderson reflected environment unavailable", error);
    }
    const titrationWorkstation = createTitrationWorkstation3D();
    titrationWorkstation.root.position.copy(TITRATION_WORKSTATION_POSITION);
    scene.add(titrationWorkstation.root);
    const titrationRaycaster = new THREE.Raycaster();
    const titrationPointer = new THREE.Vector2();

    const orbit = new OrbitControls(camera, renderer.domElement);
    orbit.enableDamping = true;
    orbit.dampingFactor = 0.07;
    orbit.screenSpacePanning = true;
    orbit.minDistance = 1.2;
    orbit.maxDistance = 36;
    orbit.maxPolarAngle = Math.PI * 0.495;

    const walk = new PointerLockControls(camera, renderer.domElement);
    const clock = new THREE.Clock();
    const keys = new Set<string>();
    const obstacles: Obstacle[] = [];
    const modelBox = new THREE.Box3();
    const mobileMove = new THREE.Vector2();
    let engineMode: EngineMode = "orbit";
    let transition: {
      fromPosition: THREE.Vector3;
      fromTarget: THREE.Vector3;
      toPosition: THREE.Vector3;
      toTarget: THREE.Vector3;
      elapsed: number;
      duration: number;
      fromFov: number;
      toFov: number;
      completionMode: "orbit" | "experiment";
    } | null = null;
    let savedWalkPosition: THREE.Vector3 | null = null;
    let savedWalkQuaternion: THREE.Quaternion | null = null;
    let cutawayRoot: THREE.Object3D | null = null;
    let fullRoot: THREE.Object3D | null = null;
    let fullLoadPromise: Promise<THREE.Object3D> | null = null;
    let overviewReady = false;
    let mobileWalk = false;
    let joystickPointerId: number | null = null;
    let lookPointerId: number | null = null;
    let lookLastX = 0;
    let lookLastY = 0;
    let jumpVelocity = 0;
    let experimentReturnMode: Mode = "orbit";
    let currentExperimentId: ExperimentId | null = null;
    let lastNearbyExperiment: ExperimentId | null = null;
    let titrationTapPointerId: number | null = null;
    let titrationFlowAccumulatorMl = 0;
    let titrationFlowCommitElapsed = 0;

    const touchCapable =
      window.matchMedia("(pointer: coarse)").matches ||
      (navigator.maxTouchPoints > 0 &&
        window.matchMedia("(hover: none)").matches);

    function setModeStatus(title: string, detail: string) {
      if (!disposed) setStatus({ title, detail });
    }

    function prepareRoot(root: THREE.Object3D) {
      applyImportedModelRendering(root);
      scene.add(root);
    }
    function buildObstacles(root: THREE.Object3D) {
      obstacles.length = 0;
      root.updateMatrixWorld(true);
      root.traverse((object) => {
        const mesh = object as THREE.Mesh;
        if (!mesh.isMesh || !isColliderName(mesh.name)) return;
        const box = new THREE.Box3().setFromObject(mesh);
        if (box.isEmpty() || box.max.y < 0.15 || box.min.y > 1.8) return;
        obstacles.push({
          name: mesh.name,
          minX: box.min.x,
          maxX: box.max.x,
          minY: box.min.y,
          maxY: box.max.y,
          minZ: box.min.z,
          maxZ: box.max.z,
          landable: isLandableName(mesh.name),
        });
      });
    }

    function setCutaway(enabled: boolean) {
      if (fullRoot) setImportedModelVisibility(fullRoot, !enabled);
      if (cutawayRoot) setImportedModelVisibility(cutawayRoot, enabled);
      // Clear the foreground power block while the titration practical is active.
      for (const root of [fullRoot, cutawayRoot]) {
        if (root)
          setTitrationBenchClearance(root, currentExperimentId === "titration");
      }
    }

    function guidedFieldOfView() {
      // Give portrait visitors room context without altering Walk optics.
      const aspect = Math.max(0.25, window.innerWidth / window.innerHeight);
      const vertical =
        2 *
        Math.atan(Math.tan(THREE.MathUtils.degToRad(29)) / Math.min(1, aspect));
      return Math.min(82, THREE.MathUtils.radToDeg(vertical));
    }

    function applyResponsiveFov() {
      let nextFov =
        engineMode === "orbit" ? guidedFieldOfView() : WALK_DESKTOP_FOV;
      if (engineMode === "experiment" && currentExperimentId === "titration") {
        const portrait = window.innerWidth <= 760 && window.innerHeight > 500;
        nextFov = portrait
          ? TITRATION_PORTRAIT_FOV
          : touchCapable
          ? TITRATION_MOBILE_FOV
          : TITRATION_DESKTOP_FOV;
      } else if (touchCapable && engineMode === "walk") {
        const aspect = Math.max(0.25, window.innerWidth / window.innerHeight);
        const targetHorizontal = THREE.MathUtils.degToRad(
          WALK_TARGET_HORIZONTAL_FOV
        );
        const vertical = 2 * Math.atan(Math.tan(targetHorizontal / 2) / aspect);
        nextFov = Math.min(
          WALK_MAX_VERTICAL_FOV,
          Math.max(WALK_DESKTOP_FOV, THREE.MathUtils.radToDeg(vertical))
        );
      }
      camera.fov = nextFov;
      camera.aspect = window.innerWidth / window.innerHeight;
      if (
        engineMode === "experiment" &&
        currentExperimentId === "titration" &&
        window.innerWidth <= 760 &&
        window.innerHeight > 500
      ) {
        // Reserve the lower viewport for the guide without hiding the flask.
        camera.setViewOffset(
          window.innerWidth,
          window.innerHeight,
          0,
          window.innerHeight * 0.18,
          window.innerWidth,
          window.innerHeight
        );
      } else {
        camera.clearViewOffset();
      }
      camera.updateProjectionMatrix();
    }
    function supportHeightAt(x: number, z: number, maxHeight = Infinity) {
      let supportHeight = 0;
      for (const obstacle of obstacles) {
        if (!obstacle.landable || obstacle.maxY > maxHeight) continue;
        if (
          x >= obstacle.minX &&
          x <= obstacle.maxX &&
          z >= obstacle.minZ &&
          z <= obstacle.maxZ
        ) {
          supportHeight = Math.max(supportHeight, obstacle.maxY);
        }
      }
      return supportHeight;
    }

    function feetHeight() {
      return camera.position.y - EYE_HEIGHT;
    }

    function currentSupportHeight() {
      return supportHeightAt(
        camera.position.x,
        camera.position.z,
        feetHeight() + GROUND_SNAP_TOLERANCE
      );
    }

    function isGrounded() {
      return (
        Math.abs(feetHeight() - currentSupportHeight()) <=
          GROUND_SNAP_TOLERANCE && Math.abs(jumpVelocity) < 0.05
      );
    }

    function rememberWalkPose() {
      if (engineMode !== "walk") return;
      const supportHeight = supportHeightAt(
        camera.position.x,
        camera.position.z,
        feetHeight() + GROUND_SNAP_TOLERANCE
      );
      savedWalkPosition = camera.position.clone();
      savedWalkPosition.y = EYE_HEIGHT + supportHeight;
      savedWalkQuaternion = camera.quaternion.clone();
    }

    function restoreWalkPose() {
      jumpVelocity = 0;
      if (savedWalkPosition && savedWalkQuaternion) {
        camera.position.copy(savedWalkPosition);
        camera.quaternion.copy(savedWalkQuaternion);
        return;
      }
      camera.position.copy(B(6.95, -1.15, EYE_HEIGHT));
      camera.lookAt(B(6.7, 1.5, EYE_HEIGHT));
    }

    function resetWalkPosition() {
      if (engineMode !== "walk") return;
      jumpVelocity = 0;
      camera.position.copy(B(6.95, -1.15, EYE_HEIGHT));
      camera.lookAt(B(6.7, 1.5, EYE_HEIGHT));
      savedWalkPosition = camera.position.clone();
      savedWalkQuaternion = camera.quaternion.clone();
      keys.clear();
      resetMobileJoystick();
      setModeStatus(
        "Walk mode",
        touchCapable
          ? "Position reset · joystick moves · Jump hops · drag right side to look"
          : "Position reset · W A S D · Space jumps · R resets · Shift faster · Esc exits"
      );
    }

    function jumpPlayer() {
      if (engineMode !== "walk" || !isGrounded()) return;
      jumpVelocity = JUMP_VELOCITY;
    }

    function resetMobileJoystick() {
      mobileMove.set(0, 0);
      joystickPointerId = null;
      joystickThumbElement.style.setProperty("--joy-x", "0px");
      joystickThumbElement.style.setProperty("--joy-y", "0px");
    }

    function setMobileWalk(active: boolean) {
      mobileWalk = active;
      if (!disposed) setMobileWalkActive(active);
      if (!active) {
        resetMobileJoystick();
        lookPointerId = null;
        if (!disposed) setLookEngaged(false);
      }
    }

    function enterExperiment(experimentId: ExperimentId) {
      if (!EXPERIMENTS_AVAILABLE || orientationOpenRef.current) return;
      const enteringFromWalk = engineMode === "walk";
      if (engineMode !== "experiment") {
        experimentReturnMode = enteringFromWalk ? "walk" : "orbit";
      }
      if (enteringFromWalk) rememberWalkPose();

      transition = null;
      keys.clear();
      jumpVelocity = 0;
      orbit.enabled = false;
      setMobileWalk(false);
      engineMode = "experiment";
      currentExperimentId = experimentId;
      setActiveExperiment(experimentId);
      setExperimentMenuOpen(false);
      setResumeWalkPrompt(false);
      setNearbyExperiment(null);
      lastNearbyExperiment = null;

      if (walk.isLocked) walk.unlock();
      applyResponsiveFov();

      if (experimentId === "titration") {
        const direction = new THREE.Vector3();
        camera.getWorldDirection(direction);
        const fromTarget = camera.position
          .clone()
          .add(direction.multiplyScalar(3));
        orbit.target.copy(fromTarget);
        const workstationView =
          window.innerWidth <= 760 && window.innerHeight > 500
            ? TITRATION_WORKSTATION_PORTRAIT_VIEW
            : TITRATION_WORKSTATION_VIEW;
        transition = {
          fromPosition: camera.position.clone(),
          fromTarget,
          toPosition: workstationView.position.clone(),
          toTarget: workstationView.target.clone(),
          elapsed: 0,
          duration: 0.85,
          fromFov: camera.fov,
          toFov:
            window.innerWidth <= 760 && window.innerHeight > 500
              ? TITRATION_PORTRAIT_FOV
              : touchCapable
              ? TITRATION_MOBILE_FOV
              : TITRATION_DESKTOP_FOV,
          completionMode: "experiment",
        };
        void ensureFullModel(false)
          .then(() => {
            if (
              !disposed &&
              engineMode === "experiment" &&
              currentExperimentId === "titration"
            ) {
              setCutaway(false);
            }
          })
          .catch((error) => {
            console.warn(
              "Henderson full model unavailable for titration",
              error
            );
          });
        setModeStatus("Titration workstation", "Moving to Wet Lab bench…");
        return;
      }

      setModeStatus("Experiment mode", EXPERIMENT_STATIONS[experimentId].title);
    }

    function exitExperiment() {
      transition = null;
      titrationTapPointerId = null;
      titrationFlowAccumulatorMl = 0;
      titrationFlowCommitElapsed = 0;
      canvasElement.style.cursor = "";
      if (currentExperimentId === "titration") {
        const guided = titrationGuidedRef.current;
        if (guided.step === TitrationGuidedStep.STOP_TAP) {
          dispatchTitrationGuided({ type: "STOP_TAP" });
        } else if (guided.session.tapOpenFraction > 0) {
          dispatchTitrationGuided({
            type: "RECONCILE",
            session: { ...guided.session, tapOpenFraction: 0 },
          });
        }
        titrationWorkstation.setOverlayFocus(null);
      }
      currentExperimentId = null;
      setActiveExperiment(null);

      if (experimentReturnMode === "walk") {
        setCutaway(false);
        restoreWalkPose();
        engineMode = "walk";
        setMode("walk");
        setWalkLoading(false);
        orbit.enabled = false;
        applyResponsiveFov();

        if (touchCapable) {
          setMobileWalk(true);
          setModeStatus(
            "Walk mode",
            "Joystick moves + steers · Jump hops · drag right side to look · Reset restores start"
          );
        } else {
          setResumeWalkPrompt(true);
          setModeStatus(
            "Walk mode",
            "Position restored · click the 3D scene to resume mouse look"
          );
        }
        return;
      }

      enterOrbit(false);
    }

    function updateNearbyExperiment() {
      if (!EXPERIMENTS_AVAILABLE || engineMode !== "walk") {
        if (lastNearbyExperiment !== null) {
          lastNearbyExperiment = null;
          setNearbyExperiment(null);
        }
        return;
      }

      let nearest: ExperimentId | null = null;
      let nearestDistance = Infinity;

      for (const experimentId of Object.keys(
        EXPERIMENT_STATIONS
      ) as ExperimentId[]) {
        const station = EXPERIMENT_STATIONS[experimentId];
        const distance = Math.hypot(
          camera.position.x - station.position.x,
          camera.position.z - station.position.z
        );
        if (distance <= EXPERIMENT_PROXIMITY_M && distance < nearestDistance) {
          nearest = experimentId;
          nearestDistance = distance;
        }
      }

      if (nearest !== lastNearbyExperiment) {
        lastNearbyExperiment = nearest;
        setNearbyExperiment(nearest);
      }
    }

    function enterOrbit(preserveTarget = false) {
      rememberWalkPose();
      keys.clear();
      jumpVelocity = 0;
      engineMode = "orbit";
      setMode("orbit");
      setWalkLoading(false);
      setMobileWalk(false);
      setResumeWalkPrompt(false);
      setCutaway(true);
      applyResponsiveFov();
      orbit.enabled = true;
      if (!preserveTarget) {
        const direction = new THREE.Vector3();
        camera.getWorldDirection(direction);
        orbit.target.copy(camera.position).add(direction.multiplyScalar(3));
      }
      orbit.update();
      setModeStatus("Orbit mode", "Drag to rotate · scroll to zoom");
    }

    function goToView(view: GuideView) {
      if (!overviewReady) return;
      if (walk.isLocked) walk.unlock();
      enterOrbit(true);
      transition = {
        fromPosition: camera.position.clone(),
        fromTarget: orbit.target.clone(),
        toPosition: view.position.clone(),
        toTarget: view.target.clone(),
        elapsed: 0,
        duration: 0.95,
        fromFov: camera.fov,
        toFov: guidedFieldOfView(),
        completionMode: "orbit",
      };
      orbit.enabled = false;
      setModeStatus("Guided view", "Moving camera…");
    }

    function goToOverview(immediate = false) {
      if (!overviewReady) return;
      const size = modelBox.getSize(new THREE.Vector3());
      const center = modelBox.getCenter(new THREE.Vector3());
      const span = Math.max(size.x, size.z);
      const target = center.clone();
      target.y = 1.05;
      const position = target
        .clone()
        .add(new THREE.Vector3(span * 0.72, span * 0.62, span * 0.78));
      if (immediate) {
        camera.position.copy(position);
        orbit.target.copy(target);
        orbit.update();
        return;
      }
      goToView({ position, target });
    }
    function applyInitialView() {
      applyResponsiveFov();
      const requested = new URLSearchParams(window.location.search).get("view");
      if (!requested || requested === "overview") {
        goToOverview(true);
        return;
      }
      const slugify = (label: string) =>
        label
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "");
      const requestedSlug =
        requested.toLowerCase() === "safety"
          ? "shower-eyewash"
          : requested.toLowerCase();
      const entry = Object.entries(GUIDE_VIEWS).find(
        ([label]) => slugify(label) === requestedSlug
      );
      if (!entry) {
        goToOverview(true);
        return;
      }
      camera.position.copy(entry[1].position);
      orbit.target.copy(entry[1].target);
      orbit.update();
    }

    const loader = new GLTFLoader();

    async function ensureFullModel(announce: boolean) {
      if (fullRoot) return fullRoot;
      if (!fullLoadPromise) {
        fullLoadPromise = loader
          .loadAsync(FULL_MODEL)
          .then((loaded) => {
            if (disposed) {
              disposeRoot(loaded.scene);
              return loaded.scene;
            }
            fullRoot = loaded.scene;
            prepareRoot(fullRoot);
            setImportedModelVisibility(fullRoot, false);
            modelBox.setFromObject(fullRoot);
            buildObstacles(fullRoot);
            return fullRoot;
          })
          .catch((error) => {
            fullLoadPromise = null;
            throw error;
          });
      }
      if (announce) {
        setModeStatus("Walk mode", "Loading walkable model…");
      }
      return fullLoadPromise;
    }
    async function enterWalk() {
      if (
        orientationOpenRef.current ||
        !overviewReady ||
        engineMode === "walk-loading"
      )
        return;

      transition = null;
      orbit.enabled = false;
      engineMode = "walk-loading";
      setMode("walk");
      setWalkLoading(true);
      setModeStatus("Walk mode", "Loading walkable model…");

      // Pointer lock must be requested directly from the user gesture.
      if (!touchCapable && !walk.isLocked) {
        walk.lock();
      }

      try {
        await ensureFullModel(true);
      } catch (error) {
        console.error(error);
        if (disposed) return;
        if (walk.isLocked) walk.unlock();
        enterOrbit(false);
        setModeStatus("Orbit mode", "Walk model could not be loaded");
        return;
      }

      if (disposed || engineMode !== "walk-loading") return;
      setCutaway(false);
      restoreWalkPose();
      engineMode = "walk";
      setWalkLoading(false);
      applyResponsiveFov();

      if (touchCapable) {
        setMobileWalk(true);
        setModeStatus(
          "Walk mode",
          "Joystick moves + steers · Jump hops · drag right side to look · Reset restores start"
        );
        return;
      }
      setModeStatus(
        "Walk mode",
        walk.isLocked
          ? "W A S D · Space jumps · R resets · Shift faster · Esc exits"
          : "Click Walk again to capture the mouse"
      );
    }
    function collides(x: number, z: number) {
      const margin = PLAYER_RADIUS;
      if (x < modelBox.min.x + margin || x > modelBox.max.x - margin)
        return true;
      if (z < modelBox.min.z + margin || z > modelBox.max.z - margin)
        return true;

      const bodyMinY = feetHeight() + COLLISION_VERTICAL_CLEARANCE;
      const bodyMaxY = feetHeight() + EYE_HEIGHT;
      return obstacles.some(
        (obstacle) =>
          obstacle.maxY > bodyMinY &&
          obstacle.minY < bodyMaxY &&
          x > obstacle.minX - margin &&
          x < obstacle.maxX + margin &&
          z > obstacle.minZ - margin &&
          z < obstacle.maxZ + margin
      );
    }

    function movePlayer(delta: THREE.Vector3) {
      const position = camera.position;
      const nextX = position.x + delta.x;
      if (!collides(nextX, position.z)) position.x = nextX;
      const nextZ = position.z + delta.z;
      if (!collides(position.x, nextZ)) position.z = nextZ;
    }

    function updateVerticalMotion(deltaTime: number) {
      if (engineMode !== "walk") return;

      const previousFeetY = feetHeight();
      const supportBeforeMove = currentSupportHeight();
      if (
        Math.abs(jumpVelocity) < 0.001 &&
        Math.abs(previousFeetY - supportBeforeMove) <= GROUND_SNAP_TOLERANCE
      ) {
        camera.position.y = EYE_HEIGHT + supportBeforeMove;
        jumpVelocity = 0;
        return;
      }

      const nextFeetY = previousFeetY + jumpVelocity * deltaTime;
      camera.position.y = EYE_HEIGHT + nextFeetY;
      jumpVelocity -= GRAVITY * deltaTime;

      if (jumpVelocity <= 0) {
        const landingHeight = supportHeightAt(
          camera.position.x,
          camera.position.z,
          previousFeetY + GROUND_SNAP_TOLERANCE
        );
        if (
          previousFeetY >= landingHeight - GROUND_SNAP_TOLERANCE &&
          nextFeetY <= landingHeight + GROUND_SNAP_TOLERANCE
        ) {
          camera.position.y = EYE_HEIGHT + landingHeight;
          jumpVelocity = 0;
          return;
        }
      }

      if (feetHeight() <= 0) {
        camera.position.y = EYE_HEIGHT;
        jumpVelocity = 0;
      }
    }

    function updateWalk(deltaTime: number) {
      if (engineMode !== "walk" || (!walk.isLocked && !mobileWalk)) {
        return;
      }

      updateVerticalMotion(deltaTime);
      let forwardAmount = mobileWalk ? -mobileMove.y : 0;
      let rightAmount = 0;

      if (mobileWalk && Math.abs(mobileMove.x) > 0.02) {
        camera.rotation.y -= mobileMove.x * MOBILE_STEER_RATE * deltaTime;
      }
      if (keys.has("KeyW") || keys.has("ArrowUp")) forwardAmount += 1;
      if (keys.has("KeyS") || keys.has("ArrowDown")) forwardAmount -= 1;
      if (keys.has("KeyD") || keys.has("ArrowRight")) rightAmount += 1;
      if (keys.has("KeyA") || keys.has("ArrowLeft")) rightAmount -= 1;

      const inputMagnitude = Math.min(
        1,
        Math.hypot(forwardAmount, rightAmount)
      );
      if (!inputMagnitude) return;

      const forward = new THREE.Vector3();
      camera.getWorldDirection(forward);
      forward.y = 0;
      forward.normalize();
      const right = new THREE.Vector3()
        .crossVectors(forward, camera.up)
        .normalize();
      const movement = forward
        .multiplyScalar(forwardAmount)
        .add(right.multiplyScalar(rightAmount));
      if (movement.lengthSq() > 1) movement.normalize();
      const speed =
        keys.has("ShiftLeft") || keys.has("ShiftRight")
          ? FAST_SPEED
          : WALK_SPEED;
      movePlayer(movement.multiplyScalar(speed * deltaTime * inputMagnitude));
    }

    function updateTitrationFlow(deltaTime: number) {
      if (
        engineMode !== "experiment" ||
        currentExperimentId !== "titration" ||
        transition
      ) {
        titrationFlowAccumulatorMl = 0;
        titrationFlowCommitElapsed = 0;
        return;
      }

      const guided = titrationGuidedRef.current;
      const session = guided.session;
      if (
        guided.step !== TitrationGuidedStep.STOP_TAP ||
        session.tapOpenFraction <= 0.001 ||
        session.dispensedMl >= 24.8
      ) {
        titrationFlowAccumulatorMl = 0;
        titrationFlowCommitElapsed = 0;
        return;
      }

      titrationFlowAccumulatorMl +=
        TITRATION_MAX_FLOW_ML_PER_SECOND * session.tapOpenFraction * deltaTime;
      titrationFlowCommitElapsed += deltaTime;

      if (titrationFlowCommitElapsed < TITRATION_FLOW_COMMIT_SECONDS) return;

      const commitMl = titrationFlowAccumulatorMl;
      titrationFlowAccumulatorMl = 0;
      titrationFlowCommitElapsed = 0;
      dispatchTitrationGuided({ type: "FLOW_TICK", deltaMl: commitMl });
    }

    function updateTransition(deltaTime: number) {
      if (!transition) return;
      transition.elapsed += deltaTime;
      const raw = Math.min(transition.elapsed / transition.duration, 1);
      const t = 1 - Math.pow(1 - raw, 3);
      camera.position.lerpVectors(
        transition.fromPosition,
        transition.toPosition,
        t
      );
      orbit.target.lerpVectors(transition.fromTarget, transition.toTarget, t);
      camera.fov = THREE.MathUtils.lerp(
        transition.fromFov,
        transition.toFov,
        t
      );
      camera.updateProjectionMatrix();
      orbit.update();
      if (raw >= 1) {
        const completionMode = transition.completionMode;
        transition = null;
        if (completionMode === "experiment") {
          orbit.enabled = false;
          setModeStatus(
            "Guided titration",
            "Follow the card one step at a time"
          );
          return;
        }
        orbit.enabled = true;
        setModeStatus("Orbit mode", "Drag to rotate · scroll to zoom");
      }
    }

    function updateJoystick(event: PointerEvent) {
      const rect = joystickElement.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const maxRadius = Math.min(rect.width, rect.height) * 0.34;
      let dx = event.clientX - centerX;
      let dy = event.clientY - centerY;
      const distance = Math.hypot(dx, dy);
      if (distance > maxRadius) {
        dx = (dx / distance) * maxRadius;
        dy = (dy / distance) * maxRadius;
      }
      mobileMove.set(dx / maxRadius, dy / maxRadius);
      joystickThumbElement.style.setProperty("--joy-x", `${dx.toFixed(1)}px`);
      joystickThumbElement.style.setProperty("--joy-y", `${dy.toFixed(1)}px`);
    }

    function onJoystickDown(event: PointerEvent) {
      if (!mobileWalk || joystickPointerId !== null) return;
      event.preventDefault();
      joystickPointerId = event.pointerId;
      joystickElement.setPointerCapture?.(event.pointerId);
      updateJoystick(event);
    }
    function onJoystickMove(event: PointerEvent) {
      if (event.pointerId !== joystickPointerId) return;
      event.preventDefault();
      updateJoystick(event);
    }
    function onJoystickRelease(event: PointerEvent) {
      if (event.pointerId !== joystickPointerId) return;
      resetMobileJoystick();
    }

    function onLookDown(event: PointerEvent) {
      if (!mobileWalk || lookPointerId !== null) return;
      event.preventDefault();
      lookPointerId = event.pointerId;
      lookLastX = event.clientX;
      lookLastY = event.clientY;
      if (!disposed) setLookEngaged(true);
      lookPadElement.setPointerCapture?.(event.pointerId);
    }
    function onLookMove(event: PointerEvent) {
      if (event.pointerId !== lookPointerId) return;
      event.preventDefault();
      const dx = event.clientX - lookLastX;
      const dy = event.clientY - lookLastY;
      lookLastX = event.clientX;
      lookLastY = event.clientY;
      camera.rotation.y -= dx * 0.0042;
      camera.rotation.x -= dy * 0.0038;
      camera.rotation.x = THREE.MathUtils.clamp(camera.rotation.x, -1.28, 1.28);
      camera.rotation.z = 0;
    }
    function onLookRelease(event: PointerEvent) {
      if (event.pointerId !== lookPointerId) return;
      lookPointerId = null;
      if (!disposed) setLookEngaged(false);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (orientationOpenRef.current) return;
      if (
        engineMode === "experiment" &&
        currentExperimentId === "titration" &&
        event.code === "Escape" &&
        !event.repeat
      ) {
        event.preventDefault();
        exitExperiment();
        return;
      }
      if (engineMode === "walk" && !event.repeat) {
        if (event.code === "Space") {
          event.preventDefault();
          jumpPlayer();
        } else if (event.code === "KeyR") {
          event.preventDefault();
          resetWalkPosition();
        } else if (
          EXPERIMENTS_AVAILABLE &&
          event.code === "KeyE" &&
          lastNearbyExperiment
        ) {
          event.preventDefault();
          enterExperiment(lastNearbyExperiment);
        }
      }
      keys.add(event.code);
    }
    function onKeyUp(event: KeyboardEvent) {
      keys.delete(event.code);
    }
    function onBlur() {
      keys.clear();
      resetMobileJoystick();
    }
    function onResize() {
      if (transition?.completionMode === "orbit") {
        transition.toFov = guidedFieldOfView();
      }
      applyResponsiveFov();
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    }

    function titrationInteractionAt(event: PointerEvent) {
      const rect = canvasElement.getBoundingClientRect();
      titrationPointer.set(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      );
      titrationRaycaster.setFromCamera(titrationPointer, camera);
      const hits = titrationRaycaster.intersectObject(
        titrationWorkstation.root,
        true
      );
      return pickTitrationInteraction(hits, titrationWorkstation.root);
    }

    function guidedTapAction(): TitrationGuidedAction | null {
      const step = titrationGuidedRef.current.step;
      if (step === TitrationGuidedStep.OPEN_TAP) return { type: "OPEN_TAP" };
      if (step === TitrationGuidedStep.STOP_TAP) return { type: "STOP_TAP" };
      return null;
    }

    let pendingApparatusAction: "tap" | "swirl" | "reading" | null = null;
    function apparatusActionAt(event: PointerEvent) {
      const id = titrationInteractionAt(event)?.userData.interaction?.id;
      if (id === "burette-tap" && guidedTapAction()) return "tap";
      if (
        id === "swirl-flask" &&
        titrationGuidedRef.current.step === TitrationGuidedStep.SWIRL
      )
        return "swirl";
      if (
        id === "burette-reading" &&
        titrationGuidedRef.current.session.tapOpenFraction === 0
      )
        return "reading";
      return null;
    }
    function onTitrationPointerDown(event: PointerEvent) {
      if (
        engineMode !== "experiment" ||
        currentExperimentId !== "titration" ||
        transition ||
        !apparatusActionAt(event)
      ) {
        return;
      }
      event.preventDefault();
      pendingApparatusAction = apparatusActionAt(event);
      titrationTapPointerId = event.pointerId;
      canvasElement.setPointerCapture?.(event.pointerId);
      canvasElement.style.cursor = "pointer";
    }

    function onTitrationPointerMove(event: PointerEvent) {
      if (
        engineMode !== "experiment" ||
        currentExperimentId !== "titration" ||
        transition
      ) {
        return;
      }
      if (event.pointerId === titrationTapPointerId) {
        event.preventDefault();
        return;
      }
      canvasElement.style.cursor = apparatusActionAt(event) ? "pointer" : "";
    }

    function finishTitrationPointer(event: PointerEvent, cancelled = false) {
      if (event.pointerId !== titrationTapPointerId) return;
      event.preventDefault();
      const action = cancelled ? null : pendingApparatusAction;
      pendingApparatusAction = null;
      titrationTapPointerId = null;
      canvasElement.releasePointerCapture?.(event.pointerId);
      canvasElement.style.cursor = "";
      if (action === "tap") {
        const tapAction = guidedTapAction();
        if (tapAction) dispatchTitrationGuided(tapAction);
      } else if (action === "swirl") dispatchTitrationGuided({ type: "SWIRL" });
      else if (action === "reading") {
        setPracticeReadingMl(null);
        setReadingViewOpen(true);
      }
    }

    function onCanvasClick() {
      if (engineMode === "walk" && !touchCapable && !walk.isLocked) {
        walk.lock();
      }
    }

    const onTitrationPointerUp = (event: PointerEvent) =>
      finishTitrationPointer(event, false);
    const onTitrationPointerCancel = (event: PointerEvent) =>
      finishTitrationPointer(event, true);

    canvasElement.addEventListener("pointerdown", onTitrationPointerDown);
    canvasElement.addEventListener("pointermove", onTitrationPointerMove);
    canvasElement.addEventListener("pointerup", onTitrationPointerUp);
    canvasElement.addEventListener("pointercancel", onTitrationPointerCancel);
    canvasElement.addEventListener("click", onCanvasClick);
    joystick.addEventListener("pointerdown", onJoystickDown);
    joystick.addEventListener("pointermove", onJoystickMove);
    joystick.addEventListener("pointerup", onJoystickRelease);
    joystick.addEventListener("pointercancel", onJoystickRelease);
    lookPad.addEventListener("pointerdown", onLookDown);
    lookPad.addEventListener("pointermove", onLookMove);
    lookPad.addEventListener("pointerup", onLookRelease);
    lookPad.addEventListener("pointercancel", onLookRelease);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    window.addEventListener("resize", onResize);

    function onWalkLock() {
      if (engineMode === "walk") {
        setResumeWalkPrompt(false);
        setModeStatus(
          "Walk mode",
          "W A S D · Space jumps · R resets · Shift faster · Esc exits"
        );
      }
    }
    function onWalkUnlock() {
      if (disposed) return;
      if (engineMode !== "walk" && engineMode !== "walk-loading") return;
      enterOrbit(false);
    }

    walk.addEventListener("lock", onWalkLock);
    walk.addEventListener("unlock", onWalkUnlock);

    controllerRef.current = {
      orientation: (open) => {
        orientationOpenRef.current = open;
        keys.clear();
        if (open) {
          transition = null;
          // Keep the current camera pose while making Walk input safe.
          if (engineMode === "walk" || engineMode === "walk-loading")
            enterOrbit(false);
          setMobileWalk(false);
          setResumeWalkPrompt(false);
          if (walk.isLocked) walk.unlock();
          orbit.enabled = false;
        } else {
          orbit.enabled = engineMode === "orbit";
        }
      },
      orbit: () => {
        enterOrbit(false);
        if (walk.isLocked) walk.unlock();
      },
      walk: () => void enterWalk(),
      jump: () => jumpPlayer(),
      reset: () => resetWalkPosition(),
      experiment: (experimentId) => enterExperiment(experimentId),
      exitExperiment: () => exitExperiment(),
      focusTitration: (focus) => titrationWorkstation.setOverlayFocus(focus),
      guide: (label) => {
        if (label === "Overview") goToOverview(false);
        else if (GUIDE_VIEWS[label]) goToView(GUIDE_VIEWS[label]);
      },
    };
    const projectionForward = new THREE.Vector3();
    const projectionDirection = new THREE.Vector3();
    const apparatusBounds = new THREE.Box3();

    function projectWorldPoint(world: THREE.Vector3): OverlayPoint {
      const projected = world.clone().project(camera);
      const rect = canvasElement.getBoundingClientRect();
      camera.getWorldDirection(projectionForward);
      projectionDirection.copy(world).sub(camera.position).normalize();
      const visible =
        projected.z >= -1 &&
        projected.z <= 1 &&
        projectionForward.dot(projectionDirection) > 0.05;
      return {
        x: Number(
          (rect.left + ((projected.x + 1) / 2) * rect.width).toFixed(2)
        ),
        y: Number(
          (rect.top + ((1 - projected.y) / 2) * rect.height).toFixed(2)
        ),
        visible,
      };
    }

    function screenPointForWorld(world: THREE.Vector3) {
      const point = projectWorldPoint(world);
      return [point.x, point.y];
    }

    function projectWorldBox(box: THREE.Box3): OverlayRect | null {
      if (box.isEmpty()) return null;
      const points: OverlayPoint[] = [];
      for (const x of [box.min.x, box.max.x]) {
        for (const y of [box.min.y, box.max.y]) {
          for (const z of [box.min.z, box.max.z]) {
            points.push(projectWorldPoint(new THREE.Vector3(x, y, z)));
          }
        }
      }
      const visible = points.filter((point) => point.visible);
      if (!visible.length) return null;
      const left = Math.min(...visible.map((point) => point.x));
      const right = Math.max(...visible.map((point) => point.x));
      const top = Math.min(...visible.map((point) => point.y));
      const bottom = Math.max(...visible.map((point) => point.y));
      return {
        left,
        right,
        top,
        bottom,
        width: right - left,
        height: bottom - top,
      };
    }

    function updateTitrationOverlayLayout() {
      if (engineMode !== "experiment" || currentExperimentId !== "titration") {
        return;
      }
      const anchors = titrationWorkstation.getOverlayWorldAnchors();
      const projection: TitrationOverlayProjection = {
        transitioning: Boolean(transition),
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
          touchCapable,
        },
        anchors: {
          meniscus: projectWorldPoint(anchors.meniscus),
          tip: projectWorldPoint(anchors.tip),
          tap: projectWorldPoint(anchors.tap),
          flaskNeck: projectWorldPoint(anchors.flaskNeck),
          flaskBody: projectWorldPoint(anchors.flaskBody),
          record: projectWorldPoint(anchors.record),
        },
        apparatusBounds: projectWorldBox(
          titrationWorkstation.getApparatusWorldBounds(apparatusBounds)
        ),
      };
      titrationOverlayRef.current?.updateLayout(projection);
    }

    function middleRetortRodScreenPoint() {
      const rod = fullRoot?.getObjectByName("MASTER_Middle_Island_retort_rod");
      if (!rod) return null;
      return screenPointForWorld(rod.getWorldPosition(new THREE.Vector3()));
    }

    const debugWindow = window as typeof window & {
      __HENDERSON_VIRTUAL_TOUR__?: {
        getState: () => Record<string, unknown>;
        dispatchTitration?: (action: TitrationGuidedAction) => void;
      };
    };
    debugWindow.__HENDERSON_VIRTUAL_TOUR__ = {
      ...(EXPERIMENTS_AVAILABLE
        ? {
            dispatchTitration: (action: TitrationGuidedAction) => {
              if (EXPERIMENTS_AVAILABLE) dispatchTitrationGuided(action);
            },
          }
        : {}),
      getState: () => ({
        experimentsAvailable: EXPERIMENTS_AVAILABLE,
        orientationOpen: orientationOpenRef.current,
        ready: overviewReady,
        mode: engineMode,
        lightingMode: lightingRig.mode,
        lightingSources: lightingRig.sceneObjects.length,
        rectAreaLights: lightingRig.rectAreaLights.length,
        transitioning: Boolean(transition),
        locked: walk.isLocked,
        camera: camera.position
          .toArray()
          .map((value) => Number(value.toFixed(4))),
        colliders: obstacles.length,
        touchCapable,
        mobileWalkActive: mobileWalk,
        mobileMove: mobileMove
          .toArray()
          .map((value) => Number(value.toFixed(3))),
        jumpVelocity: Number(jumpVelocity.toFixed(3)),
        grounded: isGrounded(),
        supportHeight: Number(currentSupportHeight().toFixed(3)),
        nearbyExperiment: lastNearbyExperiment,
        activeExperiment: currentExperimentId,
        experimentReturnMode,
        experimentSessions: {
          titration: titrationGuidedRef.current.session,
          electrical: electricalSessionRef.current,
        },
        titrationGuided: titrationGuidedRef.current,
        titrationWorkstation: titrationWorkstation.getDebugState(),
        titrationTapScreen: screenPointForWorld(
          titrationWorkstation.getTapWorldPosition()
        ),
        titrationBuretteScreen: screenPointForWorld(
          titrationWorkstation.getBuretteWorldPosition()
        ),
        titrationFlaskScreen: screenPointForWorld(
          titrationWorkstation.getFlaskWorldPosition()
        ),
        titrationLiquidSurfaceScreen: screenPointForWorld(
          titrationWorkstation.getFlaskLiquidSurfaceWorldPosition()
        ),
        titrationRetortRodScreen: middleRetortRodScreenPoint(),
        titrationOverlay: titrationOverlayRef.current?.getDebugState() ?? null,
        cameraFov: Number(camera.fov.toFixed(2)),
        landableSurfaces: obstacles
          .filter((obstacle) => obstacle.landable)
          .map((obstacle) => ({
            name: obstacle.name,
            top: Number(obstacle.maxY.toFixed(3)),
            minX: Number(obstacle.minX.toFixed(3)),
            maxX: Number(obstacle.maxX.toFixed(3)),
            minZ: Number(obstacle.minZ.toFixed(3)),
            maxZ: Number(obstacle.maxZ.toFixed(3)),
          })),
        fullModelReady: Boolean(fullRoot),
      }),
    };

    async function loadOverview() {
      try {
        const cutaway = await loader.loadAsync(CUTAWAY_MODEL);
        if (disposed) return;
        cutawayRoot = cutaway.scene;
        prepareRoot(cutawayRoot);
        modelBox.setFromObject(cutawayRoot);
        overviewReady = true;
        applyInitialView();
        if (activeExperimentRef.current === "titration")
          enterExperiment("titration");
        setReady(true);
        setLoadingMessage("");

        // Warm the walk model shortly after the overview becomes usable.
        preloadTimer = window.setTimeout(() => {
          void ensureFullModel(false).catch((error) => {
            console.warn("Henderson walk model preload failed", error);
          });
        }, 2500);
      } catch (error) {
        console.error(error);
        if (!disposed) {
          setLoadError("The Henderson 3D model could not be loaded.");
          setLoadingMessage("");
        }
      }
    }
    void loadOverview();

    renderer.setAnimationLoop(() => {
      const deltaTime = Math.min(clock.getDelta(), 0.05);
      updateTransition(deltaTime);
      updateWalk(deltaTime);
      updateNearbyExperiment();
      updateTitrationFlow(deltaTime);
      titrationWorkstation.update(
        titrationGuidedRef.current.session,
        deltaTime,
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      );
      updateTitrationOverlayLayout();
      if (engineMode === "orbit" && !transition && !orientationOpenRef.current)
        orbit.update();
      renderer.render(scene, camera);
    });
    return () => {
      disposed = true;
      window.clearTimeout(preloadTimer);
      document.body.style.overflow = previousOverflow;
      controllerRef.current = null;
      delete debugWindow.__HENDERSON_VIRTUAL_TOUR__;
      renderer.setAnimationLoop(null);
      walk.removeEventListener("lock", onWalkLock);
      walk.removeEventListener("unlock", onWalkUnlock);
      if (walk.isLocked) walk.unlock();
      orbit.dispose();
      walk.dispose();
      canvasElement.removeEventListener("pointerdown", onTitrationPointerDown);
      canvasElement.removeEventListener("pointermove", onTitrationPointerMove);
      canvasElement.removeEventListener("pointerup", onTitrationPointerUp);
      canvasElement.removeEventListener(
        "pointercancel",
        onTitrationPointerCancel
      );
      canvasElement.removeEventListener("click", onCanvasClick);
      joystick.removeEventListener("pointerdown", onJoystickDown);
      joystick.removeEventListener("pointermove", onJoystickMove);
      joystick.removeEventListener("pointerup", onJoystickRelease);
      joystick.removeEventListener("pointercancel", onJoystickRelease);
      lookPad.removeEventListener("pointerdown", onLookDown);
      lookPad.removeEventListener("pointermove", onLookMove);
      lookPad.removeEventListener("pointerup", onLookRelease);
      lookPad.removeEventListener("pointercancel", onLookRelease);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("resize", onResize);
      lightingRig.dispose();
      scene.environment = null;
      renderEnvironment?.dispose();
      disposeRoot(cutawayRoot);
      disposeRoot(fullRoot);
      scene.remove(titrationWorkstation.root);
      titrationWorkstation.dispose();
      renderer.dispose();
    };
  }, [dispatchTitrationGuided]);

  const walking = mode === "walk";
  const titrationActive = activeExperiment === "titration";
  const guideHidden = walking || walkLoading || Boolean(activeExperiment);
  const titrationSession = titrationGuided.session;
  const currentGuidedDefinition = getTitrationGuidedStepDefinition({
    session: titrationSession,
    step: titrationGuided.step,
  });
  const displayedSnapshot = displayedGuidedSnapshot(titrationGuided);
  const displayedGuidedDefinition = getTitrationGuidedStepDefinition({
    session: displayedSnapshot.session,
    step: displayedSnapshot.step,
  });
  const guidanceTarget =
    titrationGuided.reviewIndex === null
      ? currentGuidedDefinition.target
      : null;
  const guidanceTargetLabel = targetLabelFor(guidanceTarget);
  const titrationReadingMl = buretteReadingMl(
    titrationSession.dispensedMl,
    titrationSession.eyeLevel
  );
  const displayedReadingMl = buretteReadingMl(
    displayedSnapshot.session.dispensedMl,
    displayedSnapshot.session.eyeLevel
  );

  useEffect(() => {
    if (!titrationActive) return;
    controllerRef.current?.focusTitration(guidanceTarget);
    return () => controllerRef.current?.focusTitration(null);
  }, [guidanceTarget, titrationActive]);

  function updateElectricalSession(
    update: (current: ElectricalSessionState) => ElectricalSessionState
  ) {
    setElectricalSession((current) => {
      const next = update(current);
      electricalSessionRef.current = next;
      return next;
    });
  }

  function reconcileLegacyTitrationSession(
    update: (current: typeof titrationSession) => typeof titrationSession
  ) {
    dispatchTitrationGuided({
      type: "RECONCILE",
      session: update(titrationGuidedRef.current.session),
    });
  }

  return (
    <div className={styles.shell}>
      <canvas
        ref={canvasRef}
        className={styles.canvas}
        aria-label={text(
          "Interactive 3D model of Eclat Institute's Henderson laboratory"
        )}
      />

      {loadingMessage || loadError ? (
        <div className={styles.loading} role="status">
          {loadError ? (
            <div className={styles.error}>
              <strong>
                {locale === "zh-Hans"
                  ? rolloutCopy.virtualTour.text[
                      loadError as keyof typeof rolloutCopy.virtualTour.text
                    ] ?? loadError
                  : loadError}
              </strong>
              <p>
                <Link<string> href={labDestination as Route}>
                  {text("Return to Henderson lab")}
                </Link>
              </p>
            </div>
          ) : (
            <>
              <strong>{text("Loading Henderson Lab")}</strong>
              <span>
                {locale === "zh-Hans"
                  ? rolloutCopy.virtualTour.text[
                      loadingMessage as keyof typeof rolloutCopy.virtualTour.text
                    ] ?? loadingMessage
                  : loadingMessage}
              </span>
            </>
          )}
        </div>
      ) : null}
      <header className={styles.topbar}>
        <div className={styles.brand}>
          <h1 className={styles.title}>
            <Link<string>
              href={labDestination as Route}
              className={styles.headingLink}
              aria-label={text(
                "Eclat's Henderson Lab: return to location page"
              )}
            >
              {text("Eclat's Henderson Lab")}
            </Link>
          </h1>
          {titrationActive ? (
            <TitrationGuidedCard
              state={titrationGuided}
              definition={displayedGuidedDefinition}
              readingMl={displayedReadingMl}
              onDispatch={dispatchTitrationGuided}
              onStepAway={() => controllerRef.current?.exitExperiment()}
              onReadMeniscus={() => {
                setPracticeReadingMl(null);
                setReadingViewOpen(true);
              }}
              onPracticeReading={() => {
                setPracticeReadingMl(practiceReadingSample(titrationReadingMl));
                setReadingViewOpen(true);
              }}
            />
          ) : null}
        </div>

        {!activeExperiment ? (
          <div
            className={styles.modeSwitch}
            role="group"
            aria-label={text("Tour controls")}
          >
            <button
              type="button"
              className={`${styles.modeButton} ${
                mode === "orbit" ? styles.modeButtonActive : ""
              }`}
              ref={orbitButtonRef}
              onClick={() => controllerRef.current?.orbit()}
              disabled={!ready}
              aria-pressed={mode === "orbit"}
            >
              {text("Orbit")}
            </button>
            <button
              type="button"
              className={`${styles.modeButton} ${
                mode === "walk" ? styles.modeButtonActive : ""
              }`}
              ref={walkButtonRef}
              onClick={() => controllerRef.current?.walk()}
              disabled={!ready || walkLoading}
              aria-pressed={mode === "walk"}
            >
              {walkLoading ? text("Loading…") : text("Walk")}
            </button>
            <TourOrientation
              locale={locale}
              orbitTarget={orbitButtonRef}
              walkTarget={walkButtonRef}
              viewsTarget={viewsRef}
              onOpen={() => {
                orientationOpenRef.current = true;
                setExperimentMenuOpen(false);
                controllerRef.current?.orientation(true);
              }}
              onClose={() => {
                orientationOpenRef.current = false;
                controllerRef.current?.orientation(false);
              }}
            />
          </div>
        ) : null}
      </header>

      <aside
        ref={viewsRef}
        aria-label={text("Guided views")}
        hidden={guideHidden}
        className={`${styles.guidePanel} ${
          guideHidden ? styles.guidePanelHidden : ""
        }`}
      >
        <h2 className={styles.guideTitle}>{text("Guided views")}</h2>
        <div className={styles.guideButtons}>
          {GUIDE_LABELS.map((label) => (
            <button
              key={label}
              type="button"
              className={styles.guideButton}
              onClick={() => controllerRef.current?.guide(label)}
              disabled={!ready}
            >
              {locale === "zh-Hans"
                ? rolloutCopy.virtualTour.text[
                    label as keyof typeof rolloutCopy.virtualTour.text
                  ]
                : label}
            </button>
          ))}
        </div>
      </aside>
      <div
        className={`${styles.crosshair} ${
          walking ? styles.crosshairVisible : ""
        }`}
        aria-hidden="true"
      >
        +
      </div>

      <div
        className={`${styles.mobileControls} ${
          mobileWalkActive ? styles.mobileControlsActive : ""
        }`}
        aria-hidden={mobileWalkActive ? "false" : "true"}
      >
        <div ref={lookPadRef} className={styles.mobileLookPad}>
          <span
            className={`${styles.mobileLookHint} ${
              lookEngaged ? styles.mobileLookHintHidden : ""
            }`}
          >
            {text("Drag to look")}
          </span>
        </div>
        <div
          ref={joystickRef}
          className={styles.mobileJoystick}
          aria-label={text("Walk direction")}
        >
          <div className={styles.mobileJoystickRing}>
            <div
              ref={joystickThumbRef}
              className={styles.mobileJoystickThumb}
            />
          </div>
        </div>

        <div className={styles.mobileActions}>
          <button
            type="button"
            className={styles.mobileResetButton}
            onClick={() => controllerRef.current?.reset()}
          >
            {text("Reset position")}
          </button>
          <button
            type="button"
            className={styles.mobileJumpButton}
            aria-label={text("Jump")}
            onPointerDown={(event) => {
              event.preventDefault();
              controllerRef.current?.jump();
            }}
          >
            {text("Jump")}
          </button>
        </div>
      </div>

      {walkLoading || status.detail === "Walk model could not be loaded" ? (
        <div className={styles.walkFeedback} role="status">
          {locale === "zh-Hans"
            ? rolloutCopy.virtualTour.text[
                status.detail as keyof typeof rolloutCopy.virtualTour.text
              ] ?? status.detail
            : status.detail}
        </div>
      ) : null}

      {resumeWalkPrompt && !activeExperiment ? (
        <div className={styles.resumeWalkPrompt} role="status">
          {text("Click the scene to resume walking")}
        </div>
      ) : null}

      {EXPERIMENTS_AVAILABLE && !activeExperiment ? (
        <button
          type="button"
          className={styles.experimentsButton}
          aria-label="Open virtual experiments"
          aria-expanded={experimentMenuOpen}
          onClick={() => {
            setExperimentMenuOpen((open) => !open);
          }}
        >
          Experiments
        </button>
      ) : null}

      {EXPERIMENTS_AVAILABLE && experimentMenuOpen && !activeExperiment ? (
        <section
          className={styles.experimentsMenu}
          aria-label="Virtual experiments"
        >
          <span className={styles.eyebrow}>Virtual practicals</span>
          {(Object.keys(EXPERIMENT_STATIONS) as ExperimentId[]).map(
            (experimentId) => (
              <button
                key={experimentId}
                type="button"
                onClick={() => controllerRef.current?.experiment(experimentId)}
              >
                <strong>{EXPERIMENT_STATIONS[experimentId].title}</strong>
                <span>{EXPERIMENT_STATIONS[experimentId].station}</span>
              </button>
            )
          )}
        </section>
      ) : null}

      {EXPERIMENTS_AVAILABLE && nearbyExperiment && !activeExperiment ? (
        <div className={styles.experimentPrompt}>
          <div>
            <span className={styles.eyebrow}>
              {EXPERIMENT_STATIONS[nearbyExperiment].station}
            </span>
            <strong>{EXPERIMENT_STATIONS[nearbyExperiment].title}</strong>
          </div>
          <button
            type="button"
            onClick={() => controllerRef.current?.experiment(nearbyExperiment)}
          >
            {mobileWalkActive ? "Start practical" : "Press E to start"}
          </button>
        </div>
      ) : null}

      {titrationActive ? (
        <TitrationSpatialOverlay
          ref={titrationOverlayRef}
          readingMl={titrationReadingMl}
          tapOpenFraction={titrationSession.tapOpenFraction}
          target={guidanceTarget}
          targetLabel={guidanceTargetLabel}
          onStepAway={() => controllerRef.current?.exitExperiment()}
        />
      ) : null}

      {titrationActive && readingViewOpen ? (
        <TitrationReadingView
          readingMl={practiceReadingMl ?? titrationReadingMl}
          practice={practiceReadingMl !== null}
          onClose={() => setReadingViewOpen(false)}
        />
      ) : null}

      {activeExperiment === "electrical" ? (
        <ExperimentRuntime
          experimentId="electrical"
          sessions={{
            titration: titrationSession,
            electrical: electricalSession,
          }}
          onExit={() => controllerRef.current?.exitExperiment()}
          onChangeExperiment={(experimentId) =>
            controllerRef.current?.experiment(experimentId)
          }
          onUpdateTitration={reconcileLegacyTitrationSession}
          onUpdateElectrical={updateElectricalSession}
        />
      ) : null}
    </div>
  );
}
